/**
 * Subscription & Entity Access Control Utilities
 * Bepaalt wat gebruikers kunnen met entities op basis van hun abonnement
 */

export type EntityType = 'networkclub' | 'community' | 'mastermind' | 'personal';
export type SubscriptionTier = 'free' | 'starter' | 'professional' | 'enterprise';

export interface EntityAccessRights {
  canInviteMembers: boolean;
  canMakePublic: boolean;
  canCreateEvents: boolean;
  canManageRoles: boolean;
  maxMembers: number;
  reason?: string;
}

/**
 * Check wat een gebruiker kan met een entity op basis van hun abonnement
 */
export function getEntityAccessRights(
  subscriptionTier: SubscriptionTier | undefined,
  entityType: EntityType
): EntityAccessRights {

  // Free tier - beperkte toegang
  if (!subscriptionTier || subscriptionTier === 'free') {
    return {
      canInviteMembers: false,
      canMakePublic: false,
      canCreateEvents: false,
      canManageRoles: false,
      maxMembers: 1, // Alleen de eigenaar
      reason: 'Upgrade naar een betaald abonnement om leden uit te nodigen en je ' +
              getEntityLabel(entityType) + ' publiekelijk zichtbaar te maken.'
    };
  }

  // Starter tier - basis functionaliteit
  if (subscriptionTier === 'starter') {
    return {
      canInviteMembers: true,
      canMakePublic: true,
      canCreateEvents: entityType === 'networkclub', // Alleen netwerkclubs kunnen events maken
      canManageRoles: false, // Geen rol management in starter
      maxMembers: 25,
      reason: undefined
    };
  }

  // Professional tier - volledige functionaliteit
  if (subscriptionTier === 'professional') {
    return {
      canInviteMembers: true,
      canMakePublic: true,
      canCreateEvents: true,
      canManageRoles: true,
      maxMembers: 100,
      reason: undefined
    };
  }

  // Enterprise tier - onbeperkt
  if (subscriptionTier === 'enterprise') {
    return {
      canInviteMembers: true,
      canMakePublic: true,
      canCreateEvents: true,
      canManageRoles: true,
      maxMembers: Infinity,
      reason: undefined
    };
  }

  // Fallback naar free
  return getEntityAccessRights('free', entityType);
}

/**
 * Check of een gebruiker een nieuwe entity mag aanmaken
 */
export function canCreateEntity(
  subscriptionTier: SubscriptionTier | undefined,
  currentEntityCount: number
): { allowed: boolean; reason?: string; maxEntities: number } {

  if (!subscriptionTier || subscriptionTier === 'free') {
    return {
      allowed: currentEntityCount < 1,
      maxEntities: 1,
      reason: currentEntityCount >= 1
        ? 'Je hebt het maximum aantal gratis entities bereikt. Upgrade voor meer.'
        : undefined
    };
  }

  if (subscriptionTier === 'starter') {
    return {
      allowed: currentEntityCount < 3,
      maxEntities: 3,
      reason: currentEntityCount >= 3
        ? 'Je hebt het maximum van 3 entities bereikt. Upgrade naar Professional voor meer.'
        : undefined
    };
  }

  if (subscriptionTier === 'professional') {
    return {
      allowed: currentEntityCount < 10,
      maxEntities: 10,
      reason: currentEntityCount >= 10
        ? 'Je hebt het maximum van 10 entities bereikt. Contact ons voor Enterprise.'
        : undefined
    };
  }

  // Enterprise - unlimited
  return {
    allowed: true,
    maxEntities: Infinity
  };
}

/**
 * Get friendly label voor entity type
 */
export function getEntityLabel(entityType: EntityType): string {
  const labels: Record<EntityType, string> = {
    networkclub: 'netwerkclub',
    community: 'community',
    mastermind: 'mastermind',
    personal: 'persoonlijk profiel'
  };
  return labels[entityType] || entityType;
}

/**
 * @deprecated Use getEntityIconComponent() instead for Phosphor icons.
 * Kept for backward compatibility in legacy components.
 */
export function getEntityIcon(entityType: EntityType): string {
  const icons: Record<EntityType, string> = {
    networkclub: '🤝',
    community: '👥',
    mastermind: '🧠',
    personal: '👤'
  };
  return icons[entityType] || '📁';
}

/**
 * Get Phosphor icon component reference for entity type.
 * Returns the component class - render with <Icon size={...} weight="regular" />.
 *
 * Usage:
 *   import { getEntityIconComponent } from '../../utils/subscriptionUtils';
 *   const TypeIcon = getEntityIconComponent('mastermind');
 *   <TypeIcon size={20} weight="regular" className="text-gray-600" />
 */
export function getEntityIconComponent(entityType: EntityType) {
  // Dynamic import to avoid circular deps - returns the actual Phosphor component
  const { ThinkIcon, CommunityIcon, PartnerIcon, ProfileIcon } = require('../components/icons');

  const iconMap: Record<EntityType, any> = {
    mastermind: ThinkIcon,       // Brain
    community: CommunityIcon,    // Users
    networkclub: PartnerIcon,    // Handshake
    personal: ProfileIcon,       // User
  };
  return iconMap[entityType] || CommunityIcon;
}

/**
 * Get beschrijving voor entity type
 */
export function getEntityDescription(entityType: EntityType): string {
  const descriptions: Record<EntityType, string> = {
    networkclub: 'Formele netwerkorganisatie met regelmatige bijeenkomsten',
    community: 'Open community rondom een thema of interesse',
    mastermind: 'Besloten groep met intensieve samenwerking en groei',
    personal: 'Jouw persoonlijke werkruimte'
  };
  return descriptions[entityType] || '';
}

/**
 * Check of entity publiek mag zijn op basis van type
 */
export function canBePublic(entityType: EntityType): boolean {
  // Mastermind is altijd prive by design
  // Personal entities zijn altijd prive
  return entityType !== 'mastermind' && entityType !== 'personal';
}

/**
 * Get upgrade message voor specifieke actie
 */
export function getUpgradeMessage(
  action: 'invite' | 'public' | 'events' | 'roles' | 'create',
  entityType?: EntityType
): string {
  const messages = {
    invite: `Upgrade je abonnement om leden uit te nodigen voor je ${entityType ? getEntityLabel(entityType) : 'organisatie'}.`,
    public: `Upgrade je abonnement om je ${entityType ? getEntityLabel(entityType) : 'organisatie'} publiekelijk zichtbaar te maken.`,
    events: `Upgrade je abonnement om events te organiseren voor je ${entityType ? getEntityLabel(entityType) : 'organisatie'}.`,
    roles: 'Upgrade naar Professional om rol management te gebruiken.',
    create: 'Upgrade je abonnement om meer organisaties aan te maken.'
  };
  return messages[action] || 'Upgrade je abonnement voor meer functionaliteit.';
}

/**
 * Get subscription tier from subscription object
 */
export function getSubscriptionTier(subscription: any): SubscriptionTier {
  if (!subscription) return 'free';

  const tier = subscription.tier || subscription.plan || subscription.type;

  if (tier === 'starter' || tier === 'basic') return 'starter';
  if (tier === 'professional' || tier === 'pro') return 'professional';
  if (tier === 'enterprise') return 'enterprise';

  return 'free';
}

/**
 * Format member count met limits
 */
export function formatMemberCount(
  currentMembers: number,
  maxMembers: number
): string {
  if (maxMembers === Infinity) {
    return `${currentMembers} leden`;
  }
  return `${currentMembers}/${maxMembers} leden`;
}
